/**
 * Builds the handler for the backend's 4001 (expired access token).
 *
 * A normal session refreshes its token. A read-only admin session has no
 * refresh token and must never gain one (any other one would switch the
 * identity), so it ends instead: the first 4001 clears the session (only
 * this tab's part of it when another tab has stored its own) and calls
 * `onImpersonationEnded`; every later 4001 of the same page (requests that
 * were in flight) is rejected without a refresh request and without a
 * second notification, until this tab signs in again (which sets the tab's
 * in-memory Authorization header; other tabs cannot do that).
 */
export const createAuthErrorHandler = ({ jwtService, onImpersonationEnded }) => {
  let impersonationEnded = false;

  return () => {
    if (impersonationEnded) {
      if (!jwtService.hasTabSession()) {
        return Promise.reject(new Error('Read-only session expired'));
      }
      impersonationEnded = false;
    }

    if (!jwtService.isImpersonating()) {
      return jwtService.signInWithRefreshToken();
    }

    impersonationEnded = true;
    const storedToken = jwtService.getAccessToken();
    if (storedToken && storedToken === jwtService.getTabAccessToken()) {
      // The stored session is this tab's read-only session: remove it.
      jwtService.logout();
    } else {
      // Another tab has stored its own session since: leave it alone.
      jwtService.clearTabSession();
    }
    onImpersonationEnded();

    return Promise.reject(new Error('Read-only session expired'));
  };
};
